import React, { useMemo, useRef } from 'react';
import { StyleSheet, View, PanResponder, Platform } from 'react-native';
import { useStrokeStore } from '../store/useStrokeStore';
import { useAccessibilityStore } from '../store/useAccessibilityStore';
import { InputStroke, InputPoint } from '@eduwrite/shared';

// SVG imports for native drawing (react-native-svg)
import Svg, { Path as SvgPath } from 'react-native-svg';

interface SkiaCanvasProps {
    strokes?: InputStroke[];
    overlay?: InputStroke[];
    readOnly?: boolean;
}

// ─────────────────────────────────────────
// Shared utility: convert InputStroke → SVG path string
// ─────────────────────────────────────────
function strokeToSvgPath(stroke: InputStroke): string {
    if (!stroke.points || stroke.points.length === 0) return '';
    const [first, ...rest] = stroke.points;
    const move = `M ${first.x} ${first.y}`;
    const lines = rest.map(p => `L ${p.x} ${p.y}`).join(' ');
    return `${move} ${lines}`;
}

// ─────────────────────────────────────────
// NATIVE CANVAS (react-native-svg)
// ─────────────────────────────────────────
export const SkiaCanvas: React.FC<SkiaCanvasProps> = ({ strokes, overlay, readOnly }) => {
    const store = useStrokeStore();
    const { startStroke, updateStroke, endStroke } = store;
    const highContrast = useAccessibilityStore((s) => s.highContrast);

    const effectiveCompletedStrokes = strokes ?? store.completedStrokes;
    const effectiveCurrentStroke = readOnly ? null : store.currentStroke;
    const effectiveOverlay: InputStroke[] | null = overlay ?? (store.teacherSuggestion?.overlay as InputStroke[] | undefined) ?? null;

    const containerRef = useRef<View>(null);
    const layoutRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });

    const panResponder = useMemo(() => PanResponder.create({
        onStartShouldSetPanResponder: () => !readOnly,
        onMoveShouldSetPanResponder: () => !readOnly,
        onPanResponderGrant: (evt) => {
            const touch = evt.nativeEvent.touches[0];
            const point: InputPoint = {
                x: touch.pageX - layoutRef.current.x,
                y: touch.pageY - layoutRef.current.y,
                pressure: (touch as any).force ?? 1,
                timestamp: Date.now(),
            };
            startStroke(point);
        },
        onPanResponderMove: (evt) => {
            const touch = evt.nativeEvent.touches[0];
            if (!touch) return;
            const point: InputPoint = {
                x: touch.pageX - layoutRef.current.x,
                y: touch.pageY - layoutRef.current.y,
                pressure: (touch as any).force ?? 1,
                timestamp: Date.now(),
            };
            updateStroke(point);
        },
        onPanResponderRelease: () => { endStroke(); },
        onPanResponderTerminate: () => { endStroke(); },
    }), [readOnly, startStroke, updateStroke, endStroke]);

    const completedStrokeColor = highContrast ? '#FFFFFF' : '#000000';
    const currentStrokeColor = highContrast ? '#FFFF00' : '#3B82F6';

    return (
        <View
            ref={containerRef}
            style={[
                styles.container,
                !readOnly && styles.interactiveContainer,
                highContrast && styles.interactiveContainerHighContrast,
                Platform.OS === 'web' && (!readOnly ? ({ touchAction: 'none' } as any) : undefined),
            ]}
            accessibilityLabel="Handwriting canvas"
            accessibilityRole="image"
            onLayout={(_e) => {
                // Record absolute position for touch coordinate normalisation
                containerRef.current?.measure((x, y, w, h, pageX, pageY) => {
                    layoutRef.current = { x: pageX, y: pageY };
                });
            }}
            {...panResponder.panHandlers}
        >
            <Svg width="100%" height="100%" style={StyleSheet.absoluteFill}>
                {/* Completed strokes */}
                {effectiveCompletedStrokes?.map((stroke, i) => (
                    <SvgPath
                        key={`c-${i}`}
                        d={strokeToSvgPath(stroke)}
                        stroke={completedStrokeColor}
                        strokeWidth={3}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        fill="none"
                    />
                ))}

                {/* Current in-progress stroke */}
                {effectiveCurrentStroke && (
                    <SvgPath
                        d={strokeToSvgPath(effectiveCurrentStroke)}
                        stroke={currentStrokeColor}
                        strokeWidth={3}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        fill="none"
                    />
                )}

                {/* Teacher overlay strokes – red */}
                {effectiveOverlay && effectiveOverlay.map((stroke, i) => (
                    <SvgPath
                        key={`t-${i}`}
                        d={strokeToSvgPath(stroke)}
                        stroke="#EF4444"
                        strokeWidth={5}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeOpacity={0.6}
                        fill="none"
                    />
                ))}
            </Svg>
        </View>
    );
};

const styles = StyleSheet.create({
    container: {
        flex: 1,
        width: '100%',
        height: '100%',
        backgroundColor: 'transparent',
    },
    interactiveContainer: {
        backgroundColor: '#F8FAFC',
        borderWidth: 2,
        borderColor: '#CBD5E1',
        borderStyle: 'dashed',
        borderRadius: 16,
        margin: 0,
    },
    interactiveContainerHighContrast: {
        backgroundColor: '#000000',
        borderColor: '#FFFF00',
    },
});
